// Sidebar.tsx
import { getNavigationByRoles } from '@/routes/navigation';
import NavItem from "@/components/ui/NavItem/NavItem";
import {User, ChevronLeft, ChevronRight} from 'lucide-react';
import { useEffect } from 'react';
import { cn } from "@/lib/utils";
import useGlobalStore from "@/stores/useGlobalStore";
import useUserStore from "@/stores/useUserStore";
import { useLocation } from "react-router-dom";
import Logo from "@/components/ui/Logo";
import logoSvg from "@/assets/logo.svg";

export function Sidebar() {
    const { isSidebarOpen, isSidebarCollapsed, toggleSidebar, toggleSidebarCollapse } = useGlobalStore();
    const { user } = useUserStore();
    const location = useLocation();

    // Get navigation items based on user roles
    const navigationItems = getNavigationByRoles(user?.roles || []);

    // Close sidebar on route change for mobile
    useEffect(() => {
        if (window.innerWidth < 768) {
            toggleSidebar(false);
        }
    }, [location.pathname, toggleSidebar]);

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                toggleSidebar(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [toggleSidebar]);

    return (
        <>
            <div
                className={cn(
                    "fixed inset-y-0 left-0 z-50 flex flex-col",
                    "transition-all duration-300 ease-in-out",
                    "bg-white border-r border-gray-200",
                    "md:relative md:translate-x-0",
                    !isSidebarOpen && "-translate-x-full",
                    isSidebarCollapsed ? "md:w-16" : "w-64"
                )}
            >
                {/* Collapse button for desktop */}
                <button
                    onClick={toggleSidebarCollapse}
                    className={cn(
                        "hidden md:flex absolute -right-3 top-20 z-50",
                        "w-6 h-6 rounded-full bg-white border border-gray-300",
                        "items-center justify-center hover:bg-gray-50",
                        "shadow-sm transition-colors"
                    )}
                    aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    {isSidebarCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-gray-600" />
                    ) : (
                        <ChevronLeft className="h-4 w-4 text-gray-600" />
                    )}
                </button>

                <div className={cn(
                    "flex h-16 shrink-0 items-center",
                    isSidebarCollapsed ? "justify-center px-1" : "justify-center px-6"
                )}>
                    {!isSidebarCollapsed && <Logo />}
                    {isSidebarCollapsed && (
                        <img 
                            src={logoSvg} 
                            alt="MindAssist Logo" 
                            className="w-11 h-11 object-contain"
                        />
                    )}
                </div>

                <nav className="flex-1 space-y-1 px-2 py-2 overflow-y-auto">
                    {navigationItems.map((item) => (
                        <NavItem key={item.name} item={item} collapsed={isSidebarCollapsed} />
                    ))}
                </nav>

                <div className="border-t border-gray-200 p-3">
                    <NavItem
                        item={{
                            name: 'Προφίλ',
                            href: '/profile',
                            icon: User,
                        }}
                        collapsed={isSidebarCollapsed}
                    />
                </div>
            </div>

            {isSidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/30 md:hidden"
                    onClick={() => toggleSidebar(false)}
                />
            )}
        </>
    );
}
